import { jwtContainsPath } from 'utils/helpers/cookies.shared';
import { DisciplineEnum } from 'types/types';

/**
 * Names of AspNetRole rows used across the system for redirect / permission /
 * UI-gating decisions. Keep in sync with backend `AspNetRoles.Name`.
 */
export const PersonRoleName = {
    Member: 'member',
    ClubAdmin: 'club_admin',
    ClubRecordAdmin: 'club_record_admin',
    ClubFunctionary: 'club_functionary',
    ClubSocialMediaManager: 'club_social_media_manager',
    Referee: 'referee',
    Delegate: 'delegate',
} as const;
export type PersonRoleName = typeof PersonRoleName[keyof typeof PersonRoleName];

type Token = string | null | undefined;
type Discipline = DisciplineEnum | string;

/**
 * Low-level check: does the JWT contain `role.{discipline}.{roleName}` claim?
 *
 * Source of truth: backend serializes `AspNetUserRoles` into the JWT role
 * claim (see backend `AccessTokenRolesSerializer`). Adding a row to that
 * table on the backend immediately reflects here once the user refreshes
 * their access token. There is therefore no need to call any extra API.
 */
export const tokenHasRole = (
    token: Token,
    discipline: Discipline,
    roleName: PersonRoleName | string
): boolean => jwtContainsPath(token, `role.${discipline}.${roleName}`);

export const isMember = (token: Token, discipline: Discipline): boolean =>
    tokenHasRole(token, discipline, PersonRoleName.Member);

export const isClubAdmin = (token: Token, discipline: Discipline): boolean =>
    tokenHasRole(token, discipline, PersonRoleName.ClubAdmin);

export const isClubRecordAdmin = (
    token: Token,
    discipline: Discipline
): boolean => tokenHasRole(token, discipline, PersonRoleName.ClubRecordAdmin);

export const isClubFunctionary = (
    token: Token,
    discipline: Discipline
): boolean => tokenHasRole(token, discipline, PersonRoleName.ClubFunctionary);

export const isReferee = (token: Token, discipline: Discipline): boolean =>
    tokenHasRole(token, discipline, PersonRoleName.Referee);

export const isDelegate = (token: Token, discipline: Discipline): boolean =>
    tokenHasRole(token, discipline, PersonRoleName.Delegate);

export const isSocialMediaManager = (
    token: Token,
    discipline: Discipline
): boolean =>
    tokenHasRole(token, discipline, PersonRoleName.ClubSocialMediaManager);

/**
 * True if the user holds any role that should land them on the IS1 entry
 * page after login (the "elevated" audience: club admins, club functionaries,
 * referees, delegates). Members-only fall through to their IS2 profile.
 */
export const hasElevatedRole = (
    token: Token,
    discipline: Discipline
): boolean =>
    isClubAdmin(token, discipline) ||
    isClubRecordAdmin(token, discipline) ||
    isClubFunctionary(token, discipline) ||
    isReferee(token, discipline) ||
    isDelegate(token, discipline);
